import React, { FC, ReactNode, useMemo, useState } from "react";
import { useAtom } from "jotai/index";
import classNames from "classnames";

import { NamedInput } from "components/NamedInput/NamedInput";
import { NamedSelect } from "components/NamedSelect/NamedSelect";
import { Option } from "components/Select/Select";
import { Divider } from "components/Divider/Divider";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";
import { useItemSearchOptions } from "hooks/useItemSearchOptions";

import {
  openedDocItemStateAtom,
  openedDocumentAtom,
} from "../../../atoms/documentsAtom";
import { allItemsAtom } from "../../../atoms/itemAtom";
import { measureUnitsAtom } from "../../../atoms/measureUnitsAtom";
import { allPriceListsAtom } from "../../../atoms/priceListAtom";
import { settingsAtom } from "../../../atoms/settingsAtom";
import useDocuments from "../../../hooks/useDocuments";

import styles from "../styles.module.scss";
interface ComponentProps {}

const ElementContainer = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  return <div className={styles.componentContainer}>{children}</div>;
};

export const DocItemModal8: FC<ComponentProps> = ({}): JSX.Element => {
  const { getItemLeftAmount } = useDocuments();
  const [allItems] = useAtom(allItemsAtom);
  const [measureUnits] = useAtom(measureUnitsAtom);
  const [openedDocItemState, setOpenedDocumentState] = useAtom(
    openedDocItemStateAtom
  );
  const [isAmountLoading, setIsAmountLoading] = useState(false);

  const [itemSearch, setItemSearch] = useState("");
  const {
    items: remoteItems,
    options: remoteItemOptions,
    isLoading: isItemsLoading,
  } = useItemSearchOptions(itemSearch, openedDocItemState.itemid);

  const itemsOptions: Option[] = useMemo(
    () => {
      const fromAtom = allItems.map((i) => ({
        value: String(i.itemid),
        label: (i as any).longname || i.name,
      }));

      if (itemSearch.trim().length > 0) {
        const selectedId = openedDocItemState.itemid
          ? String(openedDocItemState.itemid)
          : null;
        const selectedOpt = selectedId
          ? fromAtom.find((o) => o.value === selectedId)
          : undefined;
        const merged = selectedOpt
          ? [selectedOpt, ...remoteItemOptions]
          : remoteItemOptions;
        const map = new Map<string, Option>();
        merged.forEach((o) => map.set(o.value, o));
        return Array.from(map.values());
      }

      // если поиск пустой, но выбранный item не попал в первую страницу,
      // он придёт из useItemSearchOptions(selectedItemId) в remoteItemOptions
      const map = new Map<string, Option>();
      [...remoteItemOptions, ...fromAtom].forEach((o) => map.set(o.value, o));
      return Array.from(map.values());
    },
    [allItems, itemSearch, remoteItemOptions, openedDocItemState.itemid]
  );

  const measureUnitsOptions: Option[] = useMemo(
    () => measureUnits.map((i) => ({ value: i.measureid, label: i.name })),
    [measureUnits]
  );

  const itemVal: Option | undefined = useMemo(
    () =>
      itemsOptions.find((i) => +i.value === Number(openedDocItemState.itemid)),
    [itemsOptions, openedDocItemState.itemid]
  );

  const itemArtikul: string | undefined = useMemo(
    () =>
      allItems.find((i) => +i.itemid === Number(openedDocItemState.itemid))
        ?.code,
    [allItems, openedDocItemState.itemid]
  );

  const measureUnitVal: Option | undefined = useMemo(
    () =>
      measureUnitsOptions.find(
        (i) => +i.value === Number(openedDocItemState.measureid)
      ),
    [measureUnitsOptions, openedDocItemState.measureid]
  );

  const handleItem = async (val) => {
    const selectedItem =
      allItems.find((i) => String(i.itemid) === String(val?.value)) ??
      remoteItems.find((i) => String(i.itemid) === String(val?.value));
    setOpenedDocumentState({
      itemid: val?.value,
      measureid: selectedItem?.measureid || null,
    } as any);

    if (val?.value) {
      const leftAmount = await getItemLeftAmount(
        val?.value,
        setIsAmountLoading
      );
      setOpenedDocumentState({
        realamount: leftAmount.toString(),
      } as any);
    } else {
      setOpenedDocumentState({
        realamount: 0,
      } as any);
    }
  };

  return (
    <div className={styles.wrapper}>
      <ElementContainer>
        <NamedSelect
          options={itemsOptions}
          name={"Товар"}
          nameClassName={styles.inputName}
          className={styles.itemSelect}
          value={itemVal}
          onChange={handleItem}
          placeholder={"Выберите товар"}
          isSearchable
          isLoading={isItemsLoading}
          onInputChange={setItemSearch}
          filterOption={null}
        />
      </ElementContainer>
      <div className={styles.inputsRow}>
        <ElementContainer>
          <NamedSelect
            options={measureUnitsOptions}
            name={"Ед. изм"}
            nameClassName={styles.inputName}
            value={measureUnitVal}
            onChange={(val) =>
              setOpenedDocumentState({ measureid: val?.value } as any)
            }
            placeholder={"Выберите ед. изм"}
          />
        </ElementContainer>
        <ElementContainer>
          <NamedInput
            disabled
            value={itemArtikul}
            label={"Артикул"}
            isLabelRegular
            className={styles.artikulInput}
          />
        </ElementContainer>
      </div>
      <VerticalSpace size={40} />
      <Divider />
      <VerticalSpace size={40} />

      <div className={styles.inputsRow} style={{ gap: 0 }}>
        <ElementContainer>
          <NamedInput
            disabled
            label={"Кол-во учётное"}
            type={"number"}
            labelClassName={classNames(
              styles.inputName,
              styles.inputNameLong,
              styles.inputNameMarginRight
            )}
            isLabelRegular
            value={openedDocItemState.realamount || "0"}
          />
        </ElementContainer>

        {isAmountLoading && <p>Загрузка...</p>}
      </div>
      <ElementContainer>
        <NamedInput
          label={"Кол-во фактическое + букеты"}
          type={"number"}
          labelClassName={classNames(
            styles.inputName,
            styles.inputNameLong,
            styles.inputNameMarginRight
          )}
          isLabelRegular
          value={openedDocItemState.amount || "0"}
          onChange={(ev) =>
            setOpenedDocumentState({ amount: ev.target.value } as any)
          }
        />
      </ElementContainer>

      <ElementContainer>
        <NamedInput
          disabled
          label={"Расхождение"}
          type={"number"}
          labelClassName={classNames(
            styles.inputName,
            styles.inputNameLong,
            styles.inputNameMarginRight
          )}
          isLabelRegular
          value={
            (Number(openedDocItemState.amount) || 0) -
              (Number(openedDocItemState.realamount) || 0) || "0"
          }
        />
      </ElementContainer>
      <VerticalSpace size={40} />
      <Divider />
      <VerticalSpace size={40} />
      <p className={styles.infoText}>
        Если вы поменяли дату документа, но не сохранили изменения, то учётное
        количество будет высчитываться относительно последней сохранённой даты
      </p>

      <VerticalSpace size={40} />
    </div>
  );
};
